(() => {
  const launcher = document.querySelector("#artoriasLauncher");
  const stage = document.querySelector(".window-stage");
  const input = document.querySelector("#commandSearch");
  const searchShell = document.querySelector("#searchShell");
  const results = document.querySelector("#searchResults");
  const quickActions = [...document.querySelectorAll(".quick-action")];
  const colorButton = document.querySelector("#colorMatteButton");
  const colorPopover = document.querySelector("#colorPopover");
  const colorInput = document.querySelector("#matteColor");
  const colorValue = document.querySelector("#colorValue");
  const insertMatte = document.querySelector("#insertMatte");
  const labelPopover = document.querySelector("#labelPopover");
  const toast = document.querySelector("#actionToast");
  const connectionDot = document.querySelector("#connectionDot");
  const effectColorPrompt = document.querySelector("#effectColorPrompt");
  const effectColorInput = document.querySelector("#effectColorInput");
  const effectColorValue = document.querySelector("#effectColorValue");
  const effectColorApply = document.querySelector("#effectColorApply");
  const operationStatus = document.querySelector("#operationStatus");
  const operationPhase = document.querySelector("#operationPhase");
  const operationIdentifier = document.querySelector("#operationIdentifier");
  const operationLabel = document.querySelector("#operationLabel");
  const operationMessage = document.querySelector("#operationMessage");
  const operationProgressFill = document.querySelector("#operationProgressFill");
  const operationCancel = document.querySelector("#operationCancel");
  const operationDiagnostics = document.querySelector("#operationDiagnostics");

  const categoryOrder = ["video-effects", "audio-effects", "user-presets", "console-titles", "absolute-notes"];
  const categoryLabels = {
    "video-effects": "VIDEO EFFECTS",
    "audio-effects": "AUDIO EFFECTS",
    "user-presets": "USER PRESETS",
    "console-titles": "OBSIDIAN",
    "absolute-notes": "NOTES",
  };
  const favoriteQueries = new Set([
    "fa", "fav", "favs", "favorite", "favorites", "favorito", "favoritos",
  ]);

  const nativeCommands = Object.freeze({
    size: Object.freeze({
      item: Object.freeze({
        id: "artorias:size",
        name: "Size",
        category: "video-effects",
        mediaKind: "video",
        artoriasCommand: "size",
      }),
      prompt: Object.freeze({
        kind: "number",
        label: "SIZE · PORCENTAJE",
        syntax: "SIZE · PORCENTAJE",
        minimum: 1,
        maximum: 1000,
        defaultValue: 100,
      }),
    }),
    volu: Object.freeze({
      item: Object.freeze({
        id: "artorias:volu",
        name: "Volumen",
        category: "audio-effects",
        mediaKind: "audio",
        artoriasCommand: "volu",
      }),
      prompt: Object.freeze({
        kind: "number",
        label: "VOLUMEN · dB",
        syntax: "VOLUMEN · dB",
        minimum: -96,
        maximum: 15,
        defaultValue: 0,
      }),
    }),
    stroke: Object.freeze({
      item: Object.freeze({
        id: "artorias:stroke",
        name: "Stroke",
        category: "video-effects",
        mediaKind: "video",
        artoriasCommand: "stroke",
      }),
      prompt: Object.freeze({
        kind: "number",
        label: "STROKE · ANCHO",
        syntax: "STROKE · ANCHO",
        minimum: 0.1,
        maximum: 100,
        defaultValue: 5,
      }),
    }),
    tint: Object.freeze({
      item: Object.freeze({
        id: "artorias:tint",
        name: "Tint",
        category: "video-effects",
        mediaKind: "video",
        artoriasCommand: "tint",
      }),
      prompt: Object.freeze({
        kind: "color",
        label: "TINT · COLOR",
        syntax: "TINT · COLOR",
        defaultValue: "#FFFFFF",
      }),
    }),
    gauss: Object.freeze({
      item: Object.freeze({
        id: "video:AE.ADBE Gaussian Blur 2",
        name: "Gaussian Blur",
        category: "video-effects",
        mediaKind: "video",
        matchName: "AE.ADBE Gaussian Blur 2",
      }),
      prompt: Object.freeze({
        kind: "number",
        label: "BLURRINESS",
        syntax: "gauss <amount>",
        minimum: 1,
        maximum: 100,
        defaultValue: 20,
      }),
    }),
    rename: Object.freeze({
      item: Object.freeze({
        id: "artorias:rename",
        name: "Rename",
        category: "video-effects",
        mediaKind: "mixed",
        renameSelection: true,
      }),
      prompt: Object.freeze({
        kind: "text",
        label: "NAME",
        placeholder: "NAME:",
      }),
    }),
  });

  let catalog = [];
  let engineConnected = false;
  let premiereRunning = false;
  let httpListening = true;
  let engineStateLabel = "";
  let limitedMode = true;
  // Native UserDefaults owns launcher favorites; UXP owns effects/presets.
  let launcherFavorites = new Set();
  let matches = [];
  let activeIndex = 0;
  let toastTimer = null;
  let resultsWereVisible = false;
  let revealTimer = null;
  let dismissPending = false;
  let presentationID = "";
  let valuePrompt = null;
  let currentOperation = null;
  const sessionValues = new Map();

  function selectionCoversEntireInput() {
    return input.value.length > 0
      && input.selectionStart === 0
      && input.selectionEnd === input.value.length;
  }

  function selectEntireInputValue() {
    input.select();
    const complete = selectionCoversEntireInput();
    input.dataset.selectionVerified = String(complete);
    input.classList.toggle("has-complete-selection", complete);
    return complete;
  }

  input.addEventListener("focus", () => {
    post({ type: "editingText", active: true });
  });

  input.addEventListener("blur", () => {
    delete input.dataset.selectionVerified;
    input.classList.remove("has-complete-selection");
    if (!effectColorPrompt.hidden) return;
    post({ type: "editingText", active: false });
  });

  effectColorInput.addEventListener("focus", () => {
    post({ type: "editingText", active: true });
  });

  effectColorInput.addEventListener("blur", () => {
    post({ type: "editingText", active: false });
  });

  function dismissWithoutFlash() {
    if (dismissPending) return;
    dismissPending = true;
    const dismissedPresentationID = presentationID;
    clearTimeout(revealTimer);
    clearTimeout(toastTimer);
    launcher.classList.add("is-dismissing");
    results.classList.remove("is-revealing");
    // Paint the transparent state before the native panel is ordered out.
    // This prevents a one-frame glimpse of Equinox/WebKit on Escape.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!dismissPending || presentationID !== dismissedPresentationID) return;
        post({ type: "dismiss" });
      });
    });
  }

  function post(message) {
    const bridge = window.webkit?.messageHandlers?.artorias;
    if (bridge) bridge.postMessage({ ...message, presentationID });
  }

  function engineEmptyMessage() {
    if (engineConnected && catalog.length) return "Engine connected · type to search.";
    if (engineConnected) return "Engine connected · catalog still loading…";
    if (premiereRunning) {
      return "Waiting for Absolute engine · enable Premiere Developer Mode (QA) or Adobe-signed install, then quit/reopen Premiere.";
    }
    return "Limited mode · Premiere not running.";
  }

  function catalogPendingMessage(query) {
    if (engineConnected) return "Catalog not loaded yet — wait for engine delivery (publishCatalog ≠ Hero catalog).";
    if (premiereRunning) return engineEmptyMessage();
    return `No local Absolute results for “${query}” · Premiere offline.`;
  }

  function ensureEngineStatusPill() {
    let pill = document.getElementById("engineStatusPill");
    if (pill) return pill;
    const header = document.querySelector(".header");
    const dot = connectionDot;
    if (!header || !dot) return null;
    let wrap = header.querySelector(".header-status");
    if (!wrap) {
      wrap = document.createElement("div");
      wrap.className = "header-status";
      dot.replaceWith(wrap);
      wrap.append(dot);
    }
    pill = document.createElement("span");
    pill.id = "engineStatusPill";
    pill.setAttribute("role", "status");
    wrap.insertBefore(pill, dot);
    return pill;
  }

  function applyEngineConnectionUi() {
    const state = engineConnected ? "connected" : premiereRunning ? "offline" : "waiting";
    connectionDot.classList.toggle("connected", engineConnected);
    connectionDot.dataset.engine = state;
    const short = engineConnected
      ? "Engine connected"
      : premiereRunning
        ? "Premiere running · engine offline"
        : "Waiting for UXP heartbeat";
    const tip = engineConnected
      ? "Absolute UXP heartbeat lease is active."
      : premiereRunning
        ? "Premiere is running but Absolute has no heartbeat yet. On Windows: Settings → Plugins → Enable developer mode, fully quit + reopen Premiere (External UXP will not load without it), then export diagnostics."
        : "Start Premiere after Equinox install so the Absolute UXP engine can lease the Helper bridge.";
    connectionDot.setAttribute("aria-label", short);
    connectionDot.title = tip;
    const pill = ensureEngineStatusPill();
    if (pill) {
      pill.dataset.engine = state;
      pill.textContent = engineConnected ? "Engine connected" : (premiereRunning ? "engine offline" : "Limited mode");
      pill.title = tip + (limitedMode && !engineConnected ? " Limited mode: UI available, engine not connected." : "");
    }
  }

  function normalize(value) {
    return String(value ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLocaleLowerCase()
      .replace(/\s+/g, " ")
      .trim();
  }


  function isTimeQuery(value) {
    const query = normalize(value);

    return query === "time"
      || query === "tiempo"
      || query === "duration"
      || query === "duracion";
  }


  function subsequenceScore(query, candidate) {
    let queryIndex = 0;
    let gapPenalty = 0;
    let previousCandidateIndex = -1;
    for (let candidateIndex = 0; candidateIndex < candidate.length; candidateIndex += 1) {
      if (candidate[candidateIndex] !== query[queryIndex]) continue;
      if (previousCandidateIndex >= 0) gapPenalty += candidateIndex - previousCandidateIndex - 1;
      previousCandidateIndex = candidateIndex;
      queryIndex += 1;
      if (queryIndex === query.length) return 450 - gapPenalty;
    }
    return null;
  }

  function score(queryValue, item) {
    const query = normalize(queryValue);
    if (!query) return null;
    const name = normalize(item.name);
    const candidate = normalize(item.searchText || `${item.name} ${item.matchName || ""}`);
    if (name === query) return 1200;
    if (name.startsWith(query)) return 1000 - (name.length - query.length);
    const wordIndex = name.indexOf(` ${query}`);
    if (wordIndex >= 0) return 900 - wordIndex;
    const nameIndex = name.indexOf(query);
    if (nameIndex >= 0) return 800 - nameIndex;
    const candidateIndex = candidate.indexOf(query);
    if (candidateIndex >= 0) return 650 - candidateIndex;
    return subsequenceScore(query, name);
  }

  function isInlineValueEffect(item) {
    return Boolean(item?.valuePrompt);
  }

  function isUnsupportedPreset(item) {
    // Compatibility is a property of imported .prfpset entries only. Native
    // UXP effects (Gaussian Blur included) remain executable even if a catalog
    // payload happens to carry a similarly named diagnostic field.
    return item?.category === "user-presets"
      && Boolean(item?.preset)
      && item.artoriasCompatibility === "unsupported";
  }

  function resetValuePrompt({ restoreQuery = false } = {}) {
    if (!valuePrompt) return;
    const previousQuery = valuePrompt.query;
    valuePrompt = null;
    effectColorPrompt.hidden = true;
    input.hidden = false;
    input.classList.remove("is-value-mode", "has-custom-value");
    searchShell.classList.remove("is-value-mode");
    input.removeAttribute("inputmode");
    input.placeholder = "Search command or preset…";
    input.value = restoreQuery ? previousQuery : "";
    renderResults();
    requestAnimationFrame(() => {
      input.focus();
      if (restoreQuery) input.setSelectionRange(input.value.length, input.value.length);
    });
  }

  function beginValuePrompt(item) {
    const prompt = item.valuePrompt || {};
    const rememberedValue = sessionValues.get(item.artoriasCommand);
    valuePrompt = {
      item,
      query: input.value,
      kind: prompt.kind || "number",
      minimum: Number.isFinite(prompt.minimum) ? prompt.minimum : 0,
      maximum: Number.isFinite(prompt.maximum) ? prompt.maximum : 100,
      defaultValue: rememberedValue ?? prompt.defaultValue ?? 20,
    };
    if (valuePrompt.kind === "color") {
      const color = String(valuePrompt.defaultValue || "#FFFFFF").toUpperCase();
      effectColorInput.value = color.toLowerCase();
      effectColorValue.textContent = color;
      input.hidden = true;
      effectColorPrompt.hidden = false;
    } else {
      input.hidden = false;
      effectColorPrompt.hidden = true;
    }
    input.value = "";
    input.placeholder = prompt.syntax || prompt.placeholder || `${prompt.label || "VALUE"}: ${valuePrompt.defaultValue}`;
    if (["text", "transition"].includes(valuePrompt.kind)) {
      input.removeAttribute("inputmode");
    } else {
      input.setAttribute("inputmode", "decimal");
    }
    input.classList.add("is-value-mode");
    input.classList.remove("has-custom-value");
    searchShell.classList.add("is-value-mode");
    matches = [];
    activeIndex = 0;
    results.replaceChildren();
    results.hidden = true;
    results.classList.remove("is-revealing");
    resultsWereVisible = false;
    requestResize();
    requestAnimationFrame(() => (valuePrompt.kind === "color" ? effectColorInput : input).focus());
  }

  function applyValuePrompt() {
    if (!valuePrompt) return;
    if (valuePrompt.kind === "transition") {
      const side = input.value.trim().toUpperCase();
      if (side !== "IN" && side !== "OUT") {
        window.Artorias?.showNotice?.("Escribe IN u OUT y pulsa Enter.", true);
        return;
      }
      const item = { ...valuePrompt.item, transitionSide: side };
      const saved = Number(item.transitionDurationSeconds);
      const defaultValue = Number.isFinite(saved) && saved > 0 ? saved : 1;
      beginValuePrompt({ ...item, valuePrompt: {
        kind: "transition-duration", defaultValue,
        placeholder: `Duración en segundos · ${defaultValue} · Enter para aplicar`,
      } });
      return;
    }
    if (valuePrompt.kind === "transition-duration") {
      const raw = input.value.trim().replace(",", ".");
      const seconds = raw === "" ? valuePrompt.defaultValue : Number(raw);
      if ((raw !== "" && !/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(raw))
          || !Number.isFinite(seconds) || seconds <= 0) {
        window.Artorias?.showNotice?.("Escribe una duración mayor que 0 en segundos.", true);
        return;
      }
      const item = { ...valuePrompt.item, transitionDurationSeconds: seconds };
      valuePrompt = null;
      post({ type: "executeCatalog", item });
      return;
    }
    if (valuePrompt.kind === "color") {
      const color = effectColorInput.value.toUpperCase();
      const item = { ...valuePrompt.item, artoriasColor: color };
      if (item.artoriasCommand) sessionValues.set(item.artoriasCommand, color);
      valuePrompt = null;
      effectColorPrompt.hidden = true;
      input.hidden = false;
      post({ type: "executeCatalog", item });
      return;
    }
    if (valuePrompt.kind === "text") {
      const name = input.value.trim();
      if (!name) {
        window.Artorias?.showNotice?.("Enter a name.", true);
        return;
      }
      const item = { ...valuePrompt.item, artoriasValue: name };
      valuePrompt = null;
      post({ type: "executeCatalog", item });
      return;
    }
    const raw = input.value.trim().replace(",", ".");
    const parsed = raw === "" ? valuePrompt.defaultValue : Number(raw);
    if (!Number.isFinite(parsed) || parsed < valuePrompt.minimum || parsed > valuePrompt.maximum) {
      window.Artorias?.showNotice?.(`Enter a value from ${valuePrompt.minimum} to ${valuePrompt.maximum}.`, true);
      return;
    }
    const value = parsed;
    const item = { ...valuePrompt.item, artoriasValue: value };
    if (item.artoriasCommand) sessionValues.set(item.artoriasCommand, value);
    valuePrompt = null;
    post({ type: "executeCatalog", item });
  }

  function itemUsage(item) {
    return Math.max(0, Number(item?.artoriasUsageCount) || 0);
  }

  function itemIsFavorite(item) {
    return item?.heroAction ? launcherFavorites.has(item.id) : item?.artoriasFavorite === true;
  }

  function comparePreferencePriority(a, b) {
    const favoriteDelta = Number(itemIsFavorite(b)) - Number(itemIsFavorite(a));
    if (favoriteDelta) return favoriteDelta;
    const usageDelta = itemUsage(b) - itemUsage(a);
    if (usageDelta) return usageDelta;
    return String(a.name || "").localeCompare(String(b.name || ""));
  }

  function isFavoriteSearch(query) {
    return favoriteQueries.has(normalize(query));
  }

  // Catalog entries that exist only so another control can execute them (the
  // per-colour clip labels behind the Label popover) never appear in results.
  function isSearchable(item) {
    // A legacy engine action opens CurveSmith, but Absolute already publishes
    // BEZIER as a dedicated plugin launcher. Keep its native ID callable by
    // existing integrations; remove only the duplicate search result.
    return item?.hiddenFromSearch !== true
      && item?.id !== "artorias:curvesmith-read-segment";
  }

  function searchEntries(query) {
    return catalog
      .filter(isSearchable)
      .map((item) => ({ item, score: score(query, item) }))
      .filter((entry) => entry.score !== null)
      .sort((a, b) => {
        const scoreDelta = b.score - a.score;
        if (scoreDelta) return scoreDelta;
        const usageDelta = itemUsage(b.item) - itemUsage(a.item);
        if (usageDelta) return usageDelta;
        return a.item.name.localeCompare(b.item.name);
      });
  }

  function matchingFavoriteRecommendations(query, limit = 6) {
    if (!normalize(query)) return [];
    return searchEntries(query)
      .filter((entry) => itemIsFavorite(entry.item))
      .slice(0, limit)
      .map((entry) => entry.item);
  }

  function performSearch(query) {
    const parsed = parseNativeCommand(query);
    if (parsed.state === "complete" || parsed.state === "invalid") return [];
    if (isFavoriteSearch(query)) {
      return catalog.filter((item) => isSearchable(item) && itemIsFavorite(item))
        .sort(comparePreferencePriority).slice(0, 24);
    }
    const favoriteIDs = new Set(matchingFavoriteRecommendations(query).map((item) => item.id));
    const scored = searchEntries(query).filter((entry) => !favoriteIDs.has(entry.item.id));
    return categoryOrder.flatMap((category) =>
      scored.filter((entry) => entry.item.category === category).slice(0, 8).map((entry) => entry.item),
    );
  }

  function parseNativeCommand(query) {
    const match = /^(size|volu|volumen|stroke|tint|gauss|rename|renombrar)(?:\s+(.*))?$/i.exec(String(query || "").trim());
    if (!match) return { state: "none" };
    const lowered = match[1].toLowerCase();
    const keyword = lowered === "volumen" ? "volu" : lowered === "renombrar" ? "rename" : lowered;
    const definition = nativeCommands[keyword];
    const rawValue = String(match[2] || "").trim();
    if (!rawValue) return { state: "prompt", keyword, definition };
    if (definition.prompt.kind === "color") {
      const colorMatch = /^#?([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(rawValue);
      if (!colorMatch) return { state: "invalid", keyword, definition };
      return {
        state: "complete",
        keyword,
        item: { ...definition.item, artoriasColor: `#${colorMatch[1].toUpperCase()}${(colorMatch[2] || "").toUpperCase()}` },
      };
    }
    if (definition.prompt.kind === "text") {
      return {
        state: "complete",
        keyword,
        item: { ...definition.item, artoriasValue: rawValue },
      };
    }
    const normalizedValue = rawValue.replace(",", ".");
    if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(normalizedValue)) {
      return { state: "invalid", keyword, definition };
    }
    const value = Number(normalizedValue);
    if (!Number.isFinite(value)
      || value < definition.prompt.minimum
      || value > definition.prompt.maximum) {
      return { state: "invalid", keyword, definition };
    }
    return {
      state: "complete",
      keyword,
      item: { ...definition.item, artoriasValue: value },
    };
  }

  function dispatchNativeCommand(query) {
    if (isTimeQuery(query)) {
      post({
        type: "timeInfo"
      });

      return true;
    }

    

    const parsed = parseNativeCommand(query);
    if (parsed.state === "none") return false;
    if (parsed.state === "complete") {
      post({ type: "executeCatalog", item: parsed.item });
      return true;
    }
    beginValuePrompt({
      ...parsed.definition.item,
      valuePrompt: parsed.definition.prompt,
    });
    return true;
  }

  // The current Hero UI is English; the document's language is authoritative.
  // Hosts with a language selector can update it through setUILanguage().
  function uiLanguage() {
    const language = String(launcher.dataset.locale || document.documentElement.lang || navigator.language || "en").toLowerCase();
    return language.startsWith("es") ? "es" : "en";
  }

  function metaFor(item) {
    if (item?.heroAction) return uiLanguage() === "es" ? "Complemento" : "Plugin";
    if (isUnsupportedPreset(item)) return "No disponible";
    if (item?.transitionMatchName) return "Transition";
    if (item?.preset) return "Preset";
    if (item?.artoriasCommand) return "Command";
    if (item?.category === "audio-effects") return "Audio effect";
    if (item?.category === "video-effects") return "Video effect";
    return "Command";
  }

  function displayName(item) {
    const label = String(item.displayName || item.label || item.name || "");
    // Presentation only: the complete original catalog item still reaches executeCatalog.
    return label.replace(/\b(?:UXP|native|API)\b(?:\s*[·:—-]\s*)?/gi, "")
      .replace(/\b(?:AE\.ADBE|ADBE)\s*/gi, "").replace(/\b(?:artorias|premiere)\.[\w.]+/gi, "")
      .replace(/\s*[·|]\s*$/g, "").trim() || "Command";
  }

  async function queuePreferenceCommand(command, payload = {}) {
    const response = await fetch("http://127.0.0.1:17321/action", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Dark-Signal-Client": "artorias-hero",
      },
      body: JSON.stringify({
        action: "queueEngineCommand",
        payload: { command, target: "artorias", payload },
      }),
    });
    if (!response.ok) throw new Error(`Equinox returned HTTP ${response.status}.`);
    const body = await response.json().catch(() => ({}));
    if (body?.success === false || body?.error) {
      throw new Error(String(body.error || body.message || "Equinox rejected the preference change."));
    }
  }

  async function toggleFavorite(item) {
    const id = String(item?.id || "");
    if (!id) return;
    if (item.heroAction) {
      // These three entries exist in the native launcher, not in the UXP catalog.
      // Sending them to preferences.toggleFavorite silently failed on cold boots.
      if (launcherFavorites.has(id)) launcherFavorites.delete(id); else launcherFavorites.add(id);
      renderResults();
      post({ type: "toggleLauncherFavorite", id });
      return;
    }
    const previous = itemIsFavorite(item);
    catalog = catalog.map((candidate) => candidate.id === id
      ? { ...candidate, artoriasFavorite: !previous }
      : candidate);
    renderResults();
    try {
      await queuePreferenceCommand("preferences.toggleFavorite", { id });
    } catch (error) {
      catalog = catalog.map((candidate) => candidate.id === id
        ? { ...candidate, artoriasFavorite: previous }
        : candidate);
      renderResults();
      window.Artorias?.showNotice?.(`Favorite could not be saved: ${error.message}`, true);
    }
  }

  function appendResultRow(group, item, rowIndex) {
    const row = document.createElement("div");
    row.id = `result-${rowIndex}`;
    row.className = "result-row";
    row.classList.toggle("is-launcher", Boolean(item.heroAction));
    if (item.heroAction) row.classList.add(`is-launcher-${item.heroAction}`);
    row.setAttribute("role", "option");
    row.dataset.index = String(rowIndex);

    const unsupported = isUnsupportedPreset(item);
    row.classList.toggle("is-unsupported", unsupported);
    row.classList.toggle("is-available", !unsupported);
    row.setAttribute("aria-disabled", String(unsupported));
    if (unsupported) {
      row.title = item.artoriasCompatibilityReason || "Este preset todavía no está disponible.";
    }

    const name = document.createElement("span");
    name.className = "result-name";
    name.textContent = item.heroAction ? displayName(item).toLocaleUpperCase() : displayName(item);

    const favorite = document.createElement("button");
    favorite.type = "button";
    favorite.className = "favorite-toggle";
    favorite.classList.toggle("is-favorite", itemIsFavorite(item));
    favorite.textContent = itemIsFavorite(item) ? "★" : "☆";
    favorite.setAttribute("aria-label", itemIsFavorite(item)
      ? `Remove ${displayName(item)} from favorites`
      : `Add ${displayName(item)} to favorites`);
    favorite.title = itemIsFavorite(item) ? "Remove favorite" : "Add favorite";
    favorite.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      void toggleFavorite(item);
    });

    const meta = document.createElement("span");
    meta.className = "result-meta";
    const usage = itemUsage(item);
    meta.textContent = usage > 0 ? `${metaFor(item)} · ${usage}` : metaFor(item);

    row.append(name, favorite, meta);
    row.addEventListener("mouseenter", () => setActive(rowIndex));
    row.addEventListener("click", () => executeResult(rowIndex));
    group.append(row);
  }

  function appendResultGroup(labelText, items, startIndex) {
    if (!items.length) return startIndex;
    const group = document.createElement("section");
    group.className = "result-group";
    const label = document.createElement("div");
    label.className = "category-label";
    label.textContent = labelText;
    group.append(label);
    let index = startIndex;
    for (const item of items) {
      appendResultRow(group, item, index);
      index += 1;
    }
    results.append(group);
    return index;
  }

  function renderResults() {
    if (!labelPopover.hidden) { results.hidden = true; return; }
    const query = input.value.trim();
    if (!isTimeQuery(query)) timeCard.hidden = true;
    searchShell.classList.toggle("has-query", query.length > 0);

    if (isTimeQuery(query)) {
      matches = [];
      activeIndex = 0;

      results.replaceChildren();

      const hint = document.createElement("div");
      hint.className = "empty-result time-command-hint";
      hint.textContent =
        "Medir selección / In–Out   ·   ↵ Enter";

      results.append(hint);
      results.hidden = false;

      requestResize();
      return;
    }

    
    if (!query) {
      matches = [];
      activeIndex = 0;
      results.replaceChildren();
      results.hidden = true;
      results.classList.remove("is-revealing");
      resultsWereVisible = false;
      requestResize();
      return;
    }

    const searchMatches = performSearch(query);
    // Keep one authoritative array in the exact order painted on screen so a
    // mouse click, arrow navigation and Enter always target the same item.
    const favoriteRecommendations = isFavoriteSearch(query)
      ? []
      : matchingFavoriteRecommendations(query);
    const normalizedQuery = normalize(query);
    const launcherItems = [
      { id: "equinox:console-titles", name: "Obsidian", category: "console-titles", heroAction: "consoleTitles",
        aliases: ["obsidian", "obzidian", "console", "console titles", "graphic", "graphic templates", "titles"] },
      { id: "equinox:bezier", name: "Bezier", category: "absolute-tools", heroAction: "curveSmith",
        aliases: ["bezier", "curvesmith", "curve smith", "curvas"] },
      { id: "equinox:absolute-notes", name: "Notes", category: "absolute-notes", heroAction: "absoluteNotes",
        aliases: ["notes", "note", "notas", "nota"] },
    ].filter((item) => isFavoriteSearch(query)
      ? launcherFavorites.has(item.id)
      : normalizedQuery && item.aliases.some((alias) => alias.startsWith(normalizedQuery)));
    matches = isFavoriteSearch(query)
      ? [...launcherItems, ...searchMatches]
      : [...launcherItems, ...favoriteRecommendations, ...categoryOrder.flatMap((category) =>
        searchMatches.filter((item) => item.category === category))];
    activeIndex = Math.min(activeIndex, Math.max(matches.length - 1, 0));
    results.replaceChildren();
    results.hidden = false;
    const shouldReveal = !resultsWereVisible;
    resultsWereVisible = true;

    const nativeCommand = parseNativeCommand(query);
    if (nativeCommand.state !== "none") {
      const empty = document.createElement("div");
      empty.className = "empty-result";
      empty.textContent = nativeCommand.state === "complete"
        ? "Press Enter to apply this command."
        : `Press Enter to set ${nativeCommand.definition.prompt.label.toLowerCase()}.`;
      results.append(empty);
      requestResize();
      return;
    }

    if (!catalog.length && !matches.some((item) => item.heroAction)) {
      const empty = document.createElement("div");
      empty.className = "empty-result is-engine-state";
      // Never pretend "no Premiere results" when the catalog has not arrived.
      empty.textContent = catalogPendingMessage(query);
      results.append(empty);
      requestResize();
      return;
    }

    if (!matches.length) {
      const empty = document.createElement("div");
      empty.className = "empty-result";
      empty.textContent = isFavoriteSearch(query)
        ? "No favorites yet. Use the star next to any command, effect or preset."
        : (!catalog.length
          ? catalogPendingMessage(query)
          : `No Premiere results for “${query}”`);
      results.append(empty);
      requestResize();
      return;
    }

    let linearIndex = 0;
    if (isFavoriteSearch(query)) {
      appendResultGroup("FAVORITOS", matches, linearIndex);
    } else {
      linearIndex = appendResultGroup("ABSOLUTE TOOLS", launcherItems, linearIndex);
      linearIndex = appendResultGroup("FAVORITOS", favoriteRecommendations, linearIndex);
      for (const category of categoryOrder) {
        const items = searchMatches.filter((item) => item.category === category);
        linearIndex = appendResultGroup(categoryLabels[category], items, linearIndex);
      }
    }
    updateActiveRow();
    requestResize();

    if (shouldReveal) {
      clearTimeout(revealTimer);
      results.classList.remove("is-revealing");
      requestAnimationFrame(() => {
        results.classList.add("is-revealing");
        revealTimer = setTimeout(() => results.classList.remove("is-revealing"), 90);
      });
    }
  }

  function setActive(index) {
    activeIndex = Math.max(0, Math.min(index, matches.length - 1));
    updateActiveRow();
  }

  function updateActiveRow() {
    [...results.querySelectorAll(".result-row")].forEach((row, index) => {
      const active = index === activeIndex;
      row.classList.toggle("is-active", active);
      row.setAttribute("aria-selected", String(active));
      if (active) {
        const rowTop = row.offsetTop;
        const rowBottom = rowTop + row.offsetHeight;
        const viewportTop = results.scrollTop;
        const viewportBottom = viewportTop + results.clientHeight;
        if (rowTop < viewportTop) {
          results.scrollTop = rowTop;
        } else if (rowBottom > viewportBottom) {
          results.scrollTop = rowBottom - results.clientHeight;
        }
      }
    });
  }

  function executeResult(index) {
    const item = matches[index];
    if (!item) return;
    if (item.heroAction === "consoleTitles") {
      post({ type: "openConsoleTitles" });
      return;
    }
    if (item.heroAction === "absoluteNotes") {
      post({ type: "openNotes" });
      return;
    }
    if (item.heroAction === "curveSmith") {
      post({ type: "openPanel", panel: "bezier" });
      return;
    }
    if (isUnsupportedPreset(item)) {
      window.Artorias?.showNotice?.(
        item.artoriasCompatibilityReason || "This preset is not exactly representable yet. Nothing was modified.",
        true,
      );
      return;
    }
    // An anchored effect preset is not a transition. Preserve its saved timing.
    if (item.transitionMatchName || item.transitionPreset) {
      beginValuePrompt({ ...item, valuePrompt: { kind: "transition", placeholder: "IN / OUT · Enter para elegir duración" } });
      return;
    }
    if (isInlineValueEffect(item)) {
      beginValuePrompt(item);
      return;
    }
    if (item.heroAction === "labelPicker") {
      openLabelPicker();
      return;
    }
    post({ type: "executeCatalog", item });
  }

  function closeColorPicker() {
    colorPopover.hidden = true;
    colorButton.setAttribute("aria-expanded", "false");
    colorButton.classList.remove("is-selected");
    requestResize();
  }

  function toggleColorPicker() {
    const open = colorPopover.hidden;
    if (open) closeLabelPicker();
    colorPopover.hidden = !open;
    colorButton.setAttribute("aria-expanded", String(open));
    colorButton.classList.toggle("is-selected", open);
    requestResize();
    if (open) {
      requestAnimationFrame(() => requestAnimationFrame(requestResize));
    }
  }

  function closeLabelPicker() {
    if (labelPopover.hidden) return;
    labelPopover.hidden = true;
    input.hidden = false;
    renderResults();
    for (const swatch of labelPopover.querySelectorAll(".label-swatch.is-selected")) {
      swatch.classList.remove("is-selected");
    }
    requestResize();
  }

  // Shown over the hero, replacing the search field, exactly like the tint
  // prompt — not as a floating panel below the launcher.
  function openLabelPicker() {
    closeColorPicker();
    input.hidden = true;
    labelPopover.hidden = false;
    results.hidden = true;
    for (const swatch of labelSwatches) swatch.disabled = true;
    post({ type: "labelPalette" });
    requestResize();
    requestAnimationFrame(() => labelPopover.querySelector(".label-swatch")?.focus());
  }

  // Applying a label must run through the normal execute path: it dismisses the
  // panel and returns focus to Premiere first, which the Edit > Label menu
  // command requires. Firing the helper action straight from here would leave
  // the helper frontmost and Premiere would refuse it.
  function applyClipLabel(index) {
    const labels = catalog.filter(entry => entry?.nativeAction === "applyClipLabel");
    const item = labels.find(entry => Number(entry?.nativeActionPayload?.index) === Number(index))
      || labels[Number(index)];
    if (!item) {
      window.Artorias?.showNotice("Absolute Engine is not connected yet. Try again in a moment.", true);
      return;
    }
    post({ type: "executeCatalog", item: { ...item,
      nativeActionPayload: { ...item.nativeActionPayload, index: Number(index) } } });
  }

  let resizeFrame = null;

  function clearQuickActionState() {
    quickActions.forEach((button) => {
      button.classList.remove("is-pressed", "is-confirmed");
      if (button !== colorButton) button.classList.remove("is-selected");
    });
  }

  function measuredContentHeight() {
    // macOS host keeps the historical fixed height contract.
    if (!window.EquinoxWindows) return 480;

    const root = launcher || document.querySelector(".panel");
    if (!root) return 160;

    const resultsVisible = Boolean(results && !results.hidden && results.style.display !== "none");
    // Prefer measuring the compact chrome (header + search + one button row).
    // Search results are an overlay; native host expands when resultsVisible is true.
    let bottom = 0;
    const grid = root.querySelector(".grid") || root.querySelector(".quick-actions");
    const search = root.querySelector(".search-wrap") || root.querySelector("#searchShell") || root.querySelector(".search");
    const header = root.querySelector(".header");
    for (const el of [header, search, grid]) {
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (r.height > 0) bottom = Math.max(bottom, r.bottom);
    }
    if (bottom <= 0) {
      bottom = root.getBoundingClientRect().top + Math.max(root.scrollHeight, root.offsetHeight);
    }
    // Include panel padding below the button row (≈10–14px).
    const rootTop = root.getBoundingClientRect().top;
    let height = Math.ceil(bottom - rootTop + 12);
    if (resultsVisible) {
      const rr = results.getBoundingClientRect();
      if (rr.height > 0) {
        height = Math.max(height, Math.ceil(rr.bottom - rootTop + 10));
      } else {
        height = Math.max(height, Math.ceil(root.scrollHeight + 8));
      }
    }
    return Math.max(130, Math.min(height, 720));
  }

  function measureQuickActionRows() {
    const tops = new Set();
    for (const button of quickActions) {
      const r = button.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) continue;
      tops.add(Math.round(r.top));
    }
    return tops.size || 0;
  }

  let lastPublishedContentSize = { height: 0, resultsVisible: false, at: 0 };
  function publishContentSize(force = false) {
    if (!window.EquinoxWindows) return;
    if (window.__equinoxSuppressContentSize && !force) return;
    // Native Absolute host rejects sizes until windowsHeroLayoutReady; avoid poisoning logs.
    if (window.__equinoxWindowsHeroLayoutReady === false && !force) return;
    const root = launcher || document.querySelector(".panel");
    if (!root) return;
    const rect = root.getBoundingClientRect();
    const height = measuredContentHeight();
    const resultsVisible = Boolean(results && !results.hidden && results.style.display !== "none");
    const rows = measureQuickActionRows();
    const now = Date.now();
    // Hysteresis: ignore tiny height jitter unless results visibility changed.
    if (!force
      && resultsVisible === lastPublishedContentSize.resultsVisible
      && Math.abs(height - lastPublishedContentSize.height) < 10
      && now - lastPublishedContentSize.at < 200) {
      return;
    }
    lastPublishedContentSize = { height, resultsVisible, at: now };
    post({
      type: "contentSize",
      width: Math.ceil(rect.width || window.innerWidth || 0),
      height,
      contentHeight: height,
      contentWidth: Math.ceil(rect.width || 0),
      dpr: window.devicePixelRatio || 1,
      quickActionCount: quickActions.length,
      quickActionRows: rows,
      resultsVisible,
      layoutReady: window.__equinoxWindowsHeroLayoutReady === true,
    });
  }

  // Artorias is dragged from the logo strip inside the search bar, and the
  // native view decides that with a fixed hit box. Anything clickable that
  // lands inside it loses its click to the drag: it swallowed the footer
  // buttons once, and the label swatches after that. Rather than shrink the
  // box again and wait for the next control to fall in, publish where the
  // controls actually are so the native side can step around them.
  function publishDragExclusions() {
    const rects = [];
    for (const node of document.querySelectorAll('button, a[href], select, input, textarea, [contenteditable="true"], [role="button"], [role="option"], .result-row')) {
      if (node.closest("[hidden]")) continue;
      const rect = node.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) continue;
      rects.push({ x: rect.left, y: rect.top, width: rect.width, height: rect.height });
    }
    post({ type: "dragExclusions", rects });
  }

  const timeCard = document.createElement('aside');
  timeCard.id = 'absoluteTimeCard'; timeCard.hidden = true;
  timeCard.setAttribute('aria-live', 'polite');
  document.body.append(timeCard);

  function layoutContent() {
    const anchor=searchShell.getBoundingClientRect();
    const width=anchor.width;
    const left=anchor.left;
    Object.assign(results.style,{left:left+'px',right:'auto',top:(anchor.bottom+6)+'px',width:width+'px',maxHeight:Math.max(40,Math.min(220,innerHeight-anchor.bottom-24))+'px'});
    if (!timeCard.hidden) {
      const panel=launcher.getBoundingClientRect(), w=Math.min(panel.width,500);
      const h=timeCard.getBoundingClientRect().height;
      const y=Math.max(10,panel.top-h-12);
      Object.assign(timeCard.style,{left:(panel.left+(panel.width-w)/2)+'px',top:y+'px',width:w+'px'});
    }
    publishDragExclusions();
  }

  function requestResize() {
    if (resizeFrame !== null) cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = null;
      layoutContent();
      // Windows Absolute: drive native HWND height from measured DOM content.
      publishContentSize();
    });
  }

  function renderOperation(state = currentOperation) {
    // Terminal state is history, not a modal. Equinox also clears it on the
    // native side, but this defensive gate keeps older/stale payloads from ever
    // forcing the user to click a completion button before applying again.
    if (state?.terminal === true) {
      currentOperation = null;
      launcher.classList.remove("has-operation");
      operationStatus.hidden = true;
      input.disabled = false;
      results.hidden = !labelPopover.hidden || input.value.trim().length === 0;
      requestResize();
      return;
    }
    currentOperation = state && state.operationID ? { ...state } : null;
    const visible = Boolean(currentOperation);
    launcher.classList.toggle("has-operation", visible);
    operationStatus.hidden = !visible;
    if (!visible) {
      input.disabled = false;
      requestResize();
      return;
    }
    const fraction = Math.max(0, Math.min(1, Number(currentOperation.fraction) || 0));
    const outcome = String(currentOperation.outcome || currentOperation.phase || "").toLowerCase();
    operationPhase.textContent = String(currentOperation.phase || "working").replaceAll("-", " ").toUpperCase();
    operationIdentifier.textContent = `#${String(currentOperation.operationID).slice(-8)}`;
    operationLabel.textContent = String(currentOperation.label || "Absolute operation");
    operationMessage.textContent = String(currentOperation.message || "Working in Premiere…");
    operationProgressFill.style.width = `${Math.round(fraction * 100)}%`;
    operationStatus.classList.toggle("is-failed", outcome === "failed");
    operationStatus.classList.toggle("is-cancelled", outcome === "cancelled");
    operationStatus.classList.toggle("is-completed", outcome === "completed");
    operationCancel.hidden = false;
    operationCancel.disabled = currentOperation.cancelable !== true || currentOperation.cancelRequested === true;
    operationCancel.textContent = currentOperation.cancelRequested === true
      ? "CANCELLATION REQUESTED"
      : (currentOperation.cancelable === true ? "CANCEL BEFORE CHANGES" : "COMPLETING ATOMICALLY");
    operationDiagnostics.hidden = outcome !== "failed";
    input.disabled = true;
    results.hidden = true;
    requestResize();
  }

  operationCancel.addEventListener("click", () => {
    if (!currentOperation?.operationID || currentOperation.cancelable !== true) return;
    currentOperation = { ...currentOperation, cancelRequested: true, cancelable: false };
    renderOperation(currentOperation);
    post({ type: "cancelOperation", operationID: currentOperation.operationID });
  });
  operationDiagnostics.addEventListener("click", () => post({ type: "openDiagnostics" }));

  input.addEventListener("input", () => {
    delete input.dataset.selectionVerified;
    input.classList.remove("has-complete-selection");
    if (valuePrompt) {
      if (valuePrompt.kind === "number") {
        const cleaned = input.value.replace(/[^0-9.,+-]/g, "");
        if (cleaned !== input.value) input.value = cleaned;
      }
      input.classList.toggle("has-custom-value", input.value.trim().length > 0);
      requestResize();
      return;
    }
    activeIndex = 0;
    closeColorPicker();
    renderResults();
  });

  input.addEventListener("keydown", (event) => {
    if (event.metaKey && !event.ctrlKey && !event.altKey && event.key.toLowerCase() === "a") {
      const selectionApplied = selectEntireInputValue();
      if (selectionApplied) event.preventDefault();
      return;
    }
    if (valuePrompt) {
      if (event.key === "Enter") {
        event.preventDefault();
        applyValuePrompt();
      } else if (event.key === "Escape") {
        event.preventDefault();
        resetValuePrompt({ restoreQuery: true });
      }
      return;
    }
    if (event.key === "ArrowDown" && matches.length) {
      event.preventDefault();
      setActive((activeIndex + 1) % matches.length);
    } else if (event.key === "ArrowUp" && matches.length) {
      event.preventDefault();
      setActive((activeIndex - 1 + matches.length) % matches.length);
    } else if (event.key === "Enter" && dispatchNativeCommand(input.value)) {
      event.preventDefault();
    } else if (event.key === "Enter" && matches.length) {
      event.preventDefault();
      executeResult(activeIndex);
    } else if (event.key === "Escape") {
      event.preventDefault();
      if (!colorPopover.hidden) {
        closeColorPicker();
      } else if (input.value) {
        input.value = "";
        renderResults();
      } else {
        dismissWithoutFlash();
      }
    }
  });

  effectColorInput.addEventListener("input", () => {
    effectColorValue.textContent = effectColorInput.value.toUpperCase();
  });
  effectColorInput.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      applyValuePrompt();
    } else if (event.key === "Escape") {
      event.preventDefault();
      resetValuePrompt({ restoreQuery: true });
    }
  });
  effectColorApply.addEventListener("click", applyValuePrompt);

  quickActions.forEach((button) => {
    button.addEventListener("click", () => {
      if (button.dataset.panel) {
        post({ type: "openPanel", panel: button.dataset.panel });
        return;
      }
      if (valuePrompt) resetValuePrompt();
      const kind = button.dataset.action;


      if (kind === "color") {
        toggleColorPicker();
        return;
      }

      clearQuickActionState();
      button.classList.add("is-pressed", "is-confirmed");
      setTimeout(() => {
        button.classList.remove("is-pressed", "is-confirmed", "is-selected");
      }, 240);
      post({ type: "quickAction", kind });
    });
  });

  colorInput.addEventListener("input", () => {
    colorValue.textContent = colorInput.value.toUpperCase();
  });
  document.querySelectorAll(".swatch").forEach((swatch) => {
    swatch.addEventListener("click", () => {
      colorInput.value = swatch.dataset.color;
      colorValue.textContent = swatch.dataset.color.toUpperCase();
    });
  });
  insertMatte.addEventListener("click", () => {
    post({ type: "quickAction", kind: "color", color: colorInput.value });
  });
  // The search field is hidden while the swatches are up, so its own keydown
  // handler cannot see Escape. Backing out of a label is a decision not to
  // apply one, so a single Escape closes the swatches and the whole panel
  // instead of unwinding one step at a time.
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape" || labelPopover.hidden) return;
    event.preventDefault();
    closeLabelPicker();
    dismissWithoutFlash();
  });

  // Opening the picker focuses the first swatch, and a focused button applies
  // on Enter. Without a way to move that focus the keyboard could only ever
  // reach Violet, so the arrows walk the row the way the eye does.
  const labelSwatches = Array.from(document.querySelectorAll(".label-swatch"));
  labelSwatches.forEach((swatch, index) => {
    // WKWebView does not reliably paint :focus-visible for a focus moved by
    // script, so the CSS outline alone can be a no-op: pressing an arrow key
    // moves real keyboard focus (Enter still applies the right colour) with
    // nothing visibly changing. is-active tracks true focus directly through
    // these events instead, so every swatch — however it got focus — is
    // visibly marked.
    swatch.addEventListener("mouseenter", () => swatch.focus());
    swatch.addEventListener("focus", () => swatch.classList.add("is-active"));
    swatch.addEventListener("blur", () => swatch.classList.remove("is-active"));
    swatch.addEventListener("click", () => {
      // Light up the chosen swatch and hold it on screen for a beat before
      // applying: closing (and dismissing the whole panel, which applying a
      // label always does) immediately never gave the eye time to register
      // which one was picked. Every other swatch is left exactly as it was.
      swatch.classList.add("is-selected");
      setTimeout(() => {
        // Escape, a click outside, or the panel closing in the meantime
        // already ran closeLabelPicker() and cleared is-selected -- honor
        // that cancellation instead of applying a label the user backed out of.
        if (labelPopover.hidden) return;
        closeLabelPicker();
        applyClipLabel(swatch.dataset.index);
      }, 160);
    });
    swatch.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        event.stopPropagation();
        if (!swatch.disabled) swatch.click();
        return;
      }
      const step = event.key === "ArrowRight" || event.key === "ArrowDown"
        ? 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? -1
          : 0;
      let target = null;
      if (step !== 0) {
        target = labelSwatches[(index + step + labelSwatches.length) % labelSwatches.length];
      } else if (event.key === "Home") {
        target = labelSwatches[0];
      } else if (event.key === "End") {
        target = labelSwatches[labelSwatches.length - 1];
      }
      if (!target) return;
      event.preventDefault();
      target.focus();
    });
  });

  document.addEventListener("pointerdown", (event) => {
    if (!colorPopover.hidden && !colorPopover.contains(event.target) && event.target !== colorButton) {
      closeColorPicker();
    }
    if (!labelPopover.hidden && !labelPopover.contains(event.target)) {
      closeLabelPicker();
    }
  });

  window.Artorias = {
    setLabelPalette(rows = []) {
      for (const swatch of labelSwatches) {
        const row = rows.find(row => Number(row.index) === Number(swatch.dataset.index));
        swatch.disabled = !row;
        if (!row) continue;
        swatch.dataset.label = row.name;
        swatch.title = row.name;
        swatch.setAttribute('aria-label', row.name);
        swatch.style.setProperty('--label-color', row.color);
      }
      if (!labelPopover.hidden) labelSwatches.find(s => !s.disabled)?.focus();
      if (!rows.length) window.Artorias.showNotice('No se pudo leer la paleta de Premiere.', true);
    },
    showTimeInfo(payload = {}) {
      if (dismissPending || !isTimeQuery(input.value)) return;
      timeCard.replaceChildren();
      for (const [key, label] of [['selection','SELECCIÓN'],['inOut','IN / OUT']]) {
        const data=payload[key]; if (!data) continue;
        const section=document.createElement('section');
        const title=document.createElement('span'); title.className='time-title';title.textContent=label;
        const value=document.createElement('strong');value.textContent=String(data.display || '—');
        const detail=document.createElement('small');detail.textContent=String(data.human || '');
        section.append(title,value,detail);timeCard.append(section);
      }
      if (!timeCard.children.length) timeCard.textContent='No hay selección ni rango In / Out.';
      timeCard.hidden=false;layoutContent();
    },
    setUILanguage(locale) {
      launcher.dataset.locale = String(locale || "en");
      renderResults();
    },
    setLauncherFavorites(ids) {
      launcherFavorites = new Set((Array.isArray(ids) ? ids : []).filter(id =>
        ["equinox:console-titles", "equinox:bezier", "equinox:absolute-notes"].includes(id)));
      renderResults();
    },
    setCatalog(items) {
      catalog = Array.isArray(items) ? [...items] : [];
      // Native menu commands also work with a running engine from before this
      // catalog entry existed; its generic invokeMenuCommand route is unchanged.
      if (!catalog.some(item => item.id === 'artorias:add-tracks')) catalog.push({
        id: 'artorias:add-tracks', name: 'Add Tracks', category: 'video-effects',
        mediaKind: 'mixed', nativeAction: 'invokeMenuCommand',
        nativeActionPayload: { name: 'Add Tracks...' },
        searchText: 'add tracks agregar pistas añadir pistas video audio submix'
      });
      // Catalog length is not heartbeat proof — engineConnected comes from Helper lease.
      applyEngineConnectionUi();
      try {
        post({
          type: "catalogReceived",
          itemCount: catalog.length,
          source: "setCatalog",
        });
      } catch (_) { /* ignore */ }
      renderResults();
      requestResize();
    },
    receiveState(state = {}) {
      engineConnected = !!(state.engineConnected || state.engineLeased
        || state.artoriasConnectivity === "CONNECTED"
        || state.artoriasEngineRuntimeVersion === "heartbeat-active");
      premiereRunning = !!state.premiereRunning;
      httpListening = state.httpListening !== false;
      engineStateLabel = String(state.engineStateLabel || "");
      limitedMode = state.limitedMode !== false && !engineConnected;
      applyEngineConnectionUi();
      if (!catalog.length) renderResults();
    },


    showNotice(message, isError = false) {
      clearTimeout(toastTimer);
      const visibleMessage = String(message || (isError
        ? "Absolute reported an unknown error. Open Equinox diagnostics for details."
        : "Absolute completed the command."));
      toast.textContent = visibleMessage;
      toast.classList.toggle("is-error", Boolean(isError));
      toast.classList.add("is-visible");
      requestResize();
      toastTimer = setTimeout(() => {
        toast.classList.remove("is-visible");
        requestResize();
      }, isError ? 6000 : 2200);
    },
    async preparePresentation(identifier) {
      await document.fonts.ready;
      this.reset(identifier);
      // Hidden WKWebViews suspend animation frames. Flush layout explicitly.
      if (resizeFrame !== null) cancelAnimationFrame(resizeFrame);
      resizeFrame = null;
      layoutContent();
      if (presentationID === identifier) post({ type: "presentationReady", presentationID: identifier });
    },
    reset(identifier = "") {
      timeCard.hidden = true;
      presentationID = identifier;
      dismissPending = false;
      launcher.classList.remove("is-dismissing");
      valuePrompt = null;
      effectColorPrompt.hidden = true;
      input.hidden = false;
      input.value = "";
      input.placeholder = "Search command or preset…";
      input.removeAttribute("inputmode");
      input.classList.remove("is-value-mode", "has-custom-value");
      searchShell.classList.remove("is-value-mode");
      clearQuickActionState();
      closeColorPicker();
      closeLabelPicker();
      renderResults();
      renderOperation(currentOperation);
      if (!currentOperation) input.focus();
    },
    clearForConsole() { input.value = ""; renderResults(); input.blur(); },
    focus() {
      if (!currentOperation) input.focus();
    },
    setOperation(state) {
      renderOperation(state);
    },
    clearOperation(operationID = "") {
      if (operationID && currentOperation?.operationID !== operationID) return;
      currentOperation = null;
      renderOperation(null);
      if (!dismissPending) input.focus();
    },
  };

  // Windows: observing launcher size feeds contentSize → HWND resize → ResizeObserver loop.
  // Only remeasure from DOM mutations / explicit requestResize, not from host-driven resizes.
  if (typeof ResizeObserver === "function" && !window.EquinoxWindows) {
    const observer = new ResizeObserver(() => requestResize());
    observer.observe(launcher);
  }

  new MutationObserver(() => {
    if (window.EquinoxWindows) {
      // Results open/close and quick-action presses — not every attribute flicker.
      requestResize();
      return;
    }
    requestResize();
  }).observe(launcher, {
    subtree: true, childList: true, attributes: true, attributeFilter: ['hidden', 'disabled', 'class']
  });
  document.addEventListener('scroll', publishDragExclusions, true);
  renderResults();
  post({ type: "ready" });
  requestAnimationFrame(() => {
    input.focus();
    requestResize();
  });
})();
